import { RISK_CONTROL_API_PATH } from './const';
// Baxia 相关类型定义
interface BaxiaCommon {
  init: (config: BaxiaConfig) => void;
  [key: string]: any;
}
interface BaxiaConfig {
  checkApiPath?: (url: string) => boolean;
  [key: string]: any;
}
interface BaxiaInstance {
  baxiaPromptInit: boolean;
  [key: string]: any;
}
declare global {
  interface Window {
    __baxia__?: BaxiaInstance;
    baxiaCommon?: BaxiaCommon;
  }
}
// 常量定义
const DEFAULT_TIMEOUT = 10000; // 10秒
const CHECK_INTERVAL = 100; // 100ms
/**
 * 通用等待函数
 * @param checkFn 检查函数
 * @param timeout 超时时间（毫秒）
 * @param errorMessage 超时错误信息
 * @returns Promise<boolean> 返回是否成功
 */
const waitForCondition = (
  checkFn: () => boolean,
  timeout: number = DEFAULT_TIMEOUT,
  errorMessage: string = 'Operation timeout',
): Promise<boolean> => {
  return new Promise((resolve) => {
    if (checkFn()) {
      resolve(true);
      return;
    }
    const startTime = Date.now();
    const timer = setInterval(() => {
      if (checkFn()) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(timer);
        console.warn(errorMessage);
        resolve(false);
      }
    }, CHECK_INTERVAL);
  });
};
/**
 * 检查 Baxia 是否已经加载并初始化完成
 * @returns {boolean} 返回 Baxia 是否可用
 */
export const isBaxiaReady = (): boolean => {
  return !!(window.__baxia__?.baxiaPromptInit);
};
/**
 * 检查 baxiaCommon 是否已经加载完成
 * @returns {boolean} 返回 baxiaCommon 是否可用
 */
export const isBaxiaCommonReady = (): boolean => {
  return !!(window.baxiaCommon?.init && typeof window.baxiaCommon.init === 'function');
};
/**
 * 等待 baxiaCommon 加载完成
 * @param timeout 超时时间（毫秒），默认 10 秒
 * @returns Promise<boolean> 返回是否加载成功
 */
export const waitForBaxiaCommon = (timeout: number = DEFAULT_TIMEOUT): Promise<boolean> => {
  return waitForCondition(
    isBaxiaCommonReady,
    timeout,
    'baxiaCommon loading timeout',
  );
};
/**
 * 等待 Baxia 初始化完成
 * @param timeout 超时时间（毫秒），默认 10 秒
 * @returns Promise<boolean> 返回是否初始化成功
 */
export const waitForBaxiaInit = (timeout: number = DEFAULT_TIMEOUT): Promise<boolean> => {
  return waitForCondition(
    isBaxiaReady,
    timeout,
    'Baxia initialization timeout',
  );
};
/**
 * 初始化 baxiaCommon
 * @param config baxiaCommon 配置选项
 * @param timeout 超时时间（毫秒），默认 10 秒
 * @returns Promise<boolean> 返回是否初始化成功
 */
export const initBaxiaCommon = async (
  config: BaxiaConfig,
  timeout: number = DEFAULT_TIMEOUT,
): Promise<boolean> => {
  try {
    const isCommonReady = await waitForBaxiaCommon(timeout);
    if (!isCommonReady || !window.baxiaCommon) {
      console.error('Failed to load baxiaCommon: timeout');
      return false;
    }
    console.log('baxiaCommon loaded successfully, initializing...');
    window.baxiaCommon.init(config);
    console.log('baxiaCommon initialized successfully');
    return true;
  } catch (error) {
    console.error('Error during baxiaCommon initialization:', error);
    return false;
  }
};
/**
 * 初始化 Baxia（完整流程）
 * @param config baxiaCommon 配置选项
 * @param callback 初始化成功后的回调函数
 * @param timeout 超时时间（毫秒），默认 10 秒
 */
export const initBaxia = async (
  config?: BaxiaConfig,
  callback?: () => void,
  timeout: number = DEFAULT_TIMEOUT,
): Promise<void> => {
  try {
    // 如果提供了配置，先初始化 baxiaCommon
    if (config) {
      const commonInitSuccess = await initBaxiaCommon(config, timeout);
      if (!commonInitSuccess) {
        console.error('Failed to initialize baxiaCommon');
        return;
      }
    }
    // 等待 Baxia 初始化完成
    const isReady = await waitForBaxiaInit(timeout);
    if (isReady) {
      console.log('Baxia initialized successfully');
      callback?.();
    } else {
      console.error('Failed to initialize Baxia: timeout');
    }
  } catch (error) {
    console.error('Error during Baxia initialization:', error);
  }
};
/**
 * 检查 URL 是否为风控接口
 * @param url 需要检查的 URL
 * @returns 如果 URL 包含风控接口路径则返回 true
 */
export const createCheckApiPath = (url: string): boolean => {
  return url.includes(RISK_CONTROL_API_PATH);
};
/**
 * 默认的风控接口检查函数
 */
export const checkApiPath = createCheckApiPath;
// 默认导出初始化方法
export default initBaxia;